import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, RotateCcw, Trash2, Eye, EyeOff } from "lucide-react";
import { useMe } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { AdminAccess } from "@/components/AdminAccess";
import { AdminPersonas } from "@/components/AdminPersonas";
import { AdminBugReports } from "@/components/AdminBugReports";
import { AdminCosts } from "@/components/AdminCosts";
import { AdminLauncherCosts } from "@/components/AdminLauncherCosts";
import { AdminPositions } from "@/components/AdminPositions";
import { AdminUsage } from "@/components/AdminUsage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SynthesisQueue } from "@/components/SynthesisQueue";
import {
  useAdminSessions,
  useDiscardRespondent,
  useDiscardSession,
  useRestoreSession,
  useSetSessionExcluded,
} from "@/lib/useAdmin";
import type { AdminSession } from "@/lib/types";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  done: "Done",
};

const FAMILIARITY_LABELS: Record<string, string> = {
  hands_on: "hands-on",
  familiar: "familiar",
  new_to_this: "new to this",
};

/** UPN when we have one; otherwise the raw oid, which is honest about what we
 *  know. Sessions captured before V006 have no UPN stored. */
function respondentLabel(session: AdminSession): string {
  return session.respondent_upn || session.respondent;
}

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/** One session row: metadata, an expandable transcript, and the destructive
 *  controls. Discard is behind a two-step confirm — it cascades into other
 *  topics, so a misclick is not a local mistake. */
function SessionRow({ session }: { session: AdminSession }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const discard = useDiscardSession();
  const restore = useRestoreSession();
  const setExcluded = useSetSessionExcluded();
  const discarded = Boolean(session.discarded_at);
  const excluded = Boolean(session.excluded_at);
  const [excluding, setExcluding] = useState(false);
  const [note, setNote] = useState("");
  const busy = discard.isPending || restore.isPending || setExcluded.isPending;

  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-b border-bw-blue-muted/30 py-3 last:border-0",
        discarded && "opacity-60",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 font-sans text-sm font-semibold text-bw-navy">
            {session.topic_title}
            <span className="rounded-pill bg-bw-blue-muted/20 px-2 py-0.5 text-[10px] font-semibold">
              {STATUS_LABEL[session.status] ?? session.status}
            </span>
            {session.familiarity && FAMILIARITY_LABELS[session.familiarity] && (
              <span className="rounded-pill bg-bw-blue/10 px-2 py-0.5 text-[10px] font-normal">
                {FAMILIARITY_LABELS[session.familiarity]}
              </span>
            )}
            {discarded && (
              <span className="rounded-pill bg-bw-ink-muted/15 px-2 py-0.5 text-[10px] font-semibold uppercase">
                discarded
              </span>
            )}
          </span>
          <span className="font-sans text-xs text-bw-ink-muted">
            {session.group_title} · {session.signal_count} signal
            {session.signal_count === 1 ? "" : "s"} · updated {formatWhen(session.updated_at)}
            {discarded && session.discarded_by && <> · by {session.discarded_by}</>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {session.transcript.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
              {open ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
              {session.transcript.length} turn{session.transcript.length === 1 ? "" : "s"}
            </Button>
          )}
          {discarded ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => restore.mutate(session.response_id)}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              {restore.isPending ? "Restoring…" : "Restore"}
            </Button>
          ) : confirming ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() =>
                  discard.mutate(session.response_id, { onSuccess: () => setConfirming(false) })
                }
              >
                {discard.isPending ? "Discarding…" : "Confirm discard"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {excluded ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => setExcluded.mutate({ responseId: session.response_id, excluded: false })}
                >
                  <Eye className="h-4 w-4" aria-hidden />
                  {setExcluded.isPending ? "Restoring…" : "Count again"}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => setExcluding(true)}>
                  <EyeOff className="h-4 w-4" aria-hidden />
                  Exclude
                </Button>
              )}
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Discard
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Naming the cascade before it happens, not after: this is the part an
          admin can't infer from a "Discard" button. */}
      {confirming && !discarded && (
        <p className="font-sans text-xs text-bw-ink-muted">
          Removes this interview from the team positions, withdraws anything it
          logged against <em>other</em> topics, and drops its catalog feedback.{" "}
          {respondentLabel(session)} can redo the topic from scratch. Restorable.
        </p>
      )}

      {excluded && !discarded && (
        <p className="font-sans text-xs text-bw-ink-muted">
          Not counted toward the team position — by {session.excluded_by ?? "an admin"}
          {session.exclusion_note ? <> — “{session.exclusion_note}”</> : <> — no reason recorded</>}.{" "}
          {respondentLabel(session)} still sees this interview as complete.
        </p>
      )}

      {excluding && !excluded && !discarded && (
        <div className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3">
          <label className="font-sans text-xs text-bw-ink-muted" htmlFor={`note-${session.response_id}`}>
            Why should this stop counting? Admin-only — {respondentLabel(session)} is never told, and
            their interview stays exactly as it is.
          </label>
          <input
            id={`note-${session.response_id}`}
            className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm"
            value={note}
            maxLength={280}
            placeholder="e.g. answered for a service they no longer own"
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() =>
                setExcluded.mutate(
                  { responseId: session.response_id, excluded: true, note: note.trim() || undefined },
                  {
                    onSuccess: () => {
                      setExcluding(false);
                      // Clear it: a later exclude must not silently reuse this
                      // reason in a field whose only value is being accurate.
                      setNote("");
                    },
                  },
                )
              }
            >
              {setExcluded.isPending ? "Excluding…" : "Confirm exclude"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExcluding(false);
                setNote("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {(discard.isError || restore.isError || setExcluded.isError) && (
        <span className="font-sans text-xs text-bw-ink-muted">Couldn't do that — try again.</span>
      )}

      {open && (
        <div className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3">
          {session.transcript.map((turn, i) => (
            <div
              key={i}
              className={cn(
                "max-w-[85%] whitespace-pre-wrap rounded-card px-3 py-2 font-sans text-xs",
                turn.role === "user"
                  ? "self-end bg-bw-navy text-white"
                  : "self-start bg-bw-blue-muted/15 text-bw-ink",
              )}
            >
              {turn.content}
            </div>
          ))}
          {session.summary && (
            <div className="flex flex-col gap-1 border-t border-bw-blue-muted/30 pt-2">
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                In their words
              </span>
              <p className="whitespace-pre-wrap font-sans text-xs text-bw-ink">{session.summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** All of one respondent's sessions, with the bulk sweep. */
function RespondentGroup({ label, oid, sessions }: { label: string; oid: string; sessions: AdminSession[] }) {
  const [confirming, setConfirming] = useState(false);
  const discardAll = useDiscardRespondent();
  const live = sessions.filter((s) => !s.discarded_at).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-base">{label}</CardTitle>
          <span className="font-sans text-xs text-bw-ink-muted">
            {sessions.length} session{sessions.length === 1 ? "" : "s"} · {live} live
          </span>
        </div>
        {live > 0 &&
          (confirming ? (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={discardAll.isPending}
                onClick={() => discardAll.mutate(oid, { onSuccess: () => setConfirming(false) })}
              >
                {discardAll.isPending ? "Discarding…" : `Confirm — discard all ${live}`}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Discard all
            </Button>
          ))}
      </CardHeader>
      <CardContent className="pt-0">
        {confirming && (
          <p className="pb-2 font-sans text-xs text-bw-ink-muted">
            Discards every live session for this person. Undo is per-session, on
            purpose — there's no bulk restore.
          </p>
        )}
        {sessions.map((s) => (
          <SessionRow key={s.response_id} session={s} />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Admin — interview session moderation. Reachable only by admins (route wrapped
 * in ``AdminGate``, nav entry hidden, and every endpoint 403s regardless).
 *
 * This is the one screen that shows another person's interview transcript. That
 * reverses the earlier "individual takes are private to the author" position,
 * deliberately: deciding whether a session is junk means reading it. The banner
 * says so on the page rather than only in the commit history.
 */
export function AdminPage() {
  const [includeDiscarded, setIncludeDiscarded] = useState(false);
  const { data, isLoading, isError, error } = useAdminSessions(true, includeDiscarded);
  const me = useMe();

  // Group by respondent so the bulk sweep has an obvious home, preserving the
  // API's newest-first order within each group.
  const groups = useMemo(() => {
    const byOid = new Map<string, AdminSession[]>();
    for (const session of data ?? []) {
      const list = byOid.get(session.respondent) ?? [];
      list.push(session);
      byOid.set(session.respondent, list);
    }
    return [...byOid.entries()];
  }, [data]);

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Admin</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          Interview sessions across the team. Discarding one takes it out of the
          synthesized team positions, withdraws anything it logged against other
          topics, and lets the respondent redo the topic from scratch. Nothing is
          deleted — every discard is restorable.
        </p>
        <p className="flex max-w-2xl items-start gap-2 rounded-card border border-bw-blue-muted/40 p-3 font-sans text-sm text-bw-ink-muted">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          <span>
            Transcripts here are other people's. Everywhere else in North Star an
            individual take is private to its author — this screen is the
            deliberate exception, so you can judge a session before discarding it.
          </span>
        </p>
      </section>

      <AdminAccess selfOid={me.data?.oid} />

      <AdminPersonas />

      <AdminBugReports />

      <AdminPositions />

      <SynthesisQueue />

      <AdminCosts />

      <AdminLauncherCosts />

      <AdminUsage />

      <label className="flex w-fit items-center gap-2 font-sans text-sm text-bw-ink-muted">
        <input
          type="checkbox"
          checked={includeDiscarded}
          onChange={(e) => setIncludeDiscarded(e.target.checked)}
          className="h-4 w-4"
        />
        Show discarded sessions
      </label>

      {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading sessions…</p>}

      {isError && (
        <p className="font-sans text-sm text-bw-ink-muted">
          {Number(error?.status) === 503
            ? "The preferences store isn't configured in this environment."
            : "Couldn't load sessions. Try again shortly."}
        </p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-card border border-bw-blue-muted/40 p-6 font-sans text-sm text-bw-ink-muted">
          No interview sessions{includeDiscarded ? "" : " — try showing discarded ones"}.
        </div>
      )}

      <div className="flex flex-col gap-6">
        {groups.map(([oid, sessions]) => (
          <RespondentGroup
            key={oid}
            oid={oid}
            label={respondentLabel(sessions[0])}
            sessions={sessions}
          />
        ))}
      </div>
    </div>
  );
}
